(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./art-cards.js'));
  else root.ArtCardsBackEditor = factory(root.ArtCards);
})(typeof self !== 'undefined' ? self : this, function (ArtCards) {
  const BACK_KEY = 'kf_artcards_back';
  const LOGO_KEY = 'kf_artcards_logo';
  const LINES = [1, 2, 3];
  const STYLES = ['normal', 'bold', 'italic'];
  const TEXT_POSITIONS = ['top', 'bottom', 'left', 'right'];
  const LOGO_POSITIONS = ['top', 'bottom', 'left', 'right', 'center'];
  const LOGO_SIZES = Object.keys(ArtCards.LOGO_WIDTHS);
  const LOGO_DEFAULT = Object.freeze({ position: 'bottom', size: 'medium' });
  const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
  const LOGO_DATA = /^data:image\/(png|jpeg|svg\+xml|webp)[;,]/i;
  const SVG_DATA = /^data:image\/svg\+xml/i;
  const SHAPES = ['square', 'portrait', 'landscape'];

  function isObject(value){
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function copyOf(value){
    return isObject(value) ? Object.assign({}, value) : null;
  }

  function fill(text, values){
    return String(text == null ? '' : text).replace(/\{(\w+)\}/g, (match, key) =>
      values && values[key] != null ? String(values[key]) : match);
  }

  function defaults(){
    return ArtCards.emptyBack();
  }

  function validSize(line, value){
    if(typeof value !== 'number' && typeof value !== 'string') return null;
    const size = Number(value);
    return ArtCards.BACK_SIZES['line' + line].includes(size) ? size : null;
  }

  function withinLimit(text, limits, line){
    const max = Number(isObject(limits) ? limits['line' + line] : NaN);
    return !(max > 0) || text.length <= max;
  }

  function cleanBack(saved, limits){
    if(!isObject(saved)) return null;
    const back = defaults();
    LINES.forEach(n => {
      const text = saved['line' + n + '_text'];
      const font = saved['line' + n + '_font'];
      const size = validSize(n, saved['line' + n + '_size']);
      const style = saved['line' + n + '_style'];
      if(n !== 2 && typeof text === 'string' && withinLimit(text, limits, n)) back['line' + n + '_text'] = text;
      if(ArtCards.BACK_FONTS.includes(font)) back['line' + n + '_font'] = font;
      if(size != null) back['line' + n + '_size'] = size;
      if(STYLES.includes(style)) back['line' + n + '_style'] = style;
    });
    if(TEXT_POSITIONS.includes(saved.position)) back.position = saved.position;
    return back;
  }

  function cleanLogo(saved){
    if(!isObject(saved) || typeof saved.dataUrl !== 'string' || !LOGO_DATA.test(saved.dataUrl)) return null;
    return {
      key: '',
      dataUrl: saved.dataUrl,
      position: LOGO_POSITIONS.includes(saved.position) ? saved.position : LOGO_DEFAULT.position,
      size: LOGO_SIZES.includes(saved.size) ? saved.size : LOGO_DEFAULT.size
    };
  }

  function readItem(storage, key){
    try {
      const raw = storage ? storage.getItem(key) : null;
      return raw ? JSON.parse(raw) : null;
    } catch(e){
      return null;
    }
  }

  function writeItem(storage, key, value){
    try {
      if(!storage) return false;
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch(e){
      return false;
    }
  }

  function removeItem(storage, key){
    try {
      if(!storage) return false;
      storage.removeItem(key);
      return true;
    } catch(e){
      return false;
    }
  }

  function loadSaved(storage, limits){
    return { back: cleanBack(readItem(storage, BACK_KEY), limits), logo: cleanLogo(readItem(storage, LOGO_KEY)) };
  }

  function save(storage, sharedBack, sharedLogo){
    let done = true;
    if(isObject(sharedBack)){
      const back = {};
      Object.keys(defaults()).forEach(key => { back[key] = sharedBack[key]; });
      done = writeItem(storage, BACK_KEY, back) && done;
    }
    if(isObject(sharedLogo) && sharedLogo.dataUrl){
      done = writeItem(storage, LOGO_KEY, { dataUrl: sharedLogo.dataUrl, position: sharedLogo.position, size: sharedLogo.size }) && done;
    }
    return done;
  }

  function clearSaved(storage){
    const back = removeItem(storage, BACK_KEY);
    const logo = removeItem(storage, LOGO_KEY);
    return back && logo;
  }

  function hex(buffer){
    return Array.from(new Uint8Array(buffer)).map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  function byteView(bytes){
    if(bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
    if(ArrayBuffer.isView(bytes)) return new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return new Uint8Array(0);
  }

  function fallbackKey(bytes){
    const view = byteView(bytes);
    let hash = 0x811c9dc5;
    for(let i = 0; i < view.length; i += 1){
      hash ^= view[i];
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return 'fnv1a-' + hash.toString(16).padStart(8, '0') + '-' + view.length;
  }

  function logoKey(bytes){
    const subtle = typeof crypto !== 'undefined' && crypto && crypto.subtle;
    if(!subtle) return Promise.resolve(fallbackKey(bytes));
    try {
      return Promise.resolve(subtle.digest('SHA-256', bytes)).then(hex).catch(() => fallbackKey(bytes));
    } catch(e){
      return Promise.resolve(fallbackKey(bytes));
    }
  }

  function busyTracker(report){
    let count = 0;
    let shown = false;
    function flush(){
      const now = count > 0;
      if(now === shown) return;
      shown = now;
      report(now);
    }
    function settle(){
      count -= 1;
      flush();
    }
    return function track(work){
      count += 1;
      Promise.resolve().then(flush);
      Promise.resolve(work).then(settle, settle);
      return work;
    };
  }

  function binaryBytes(binary){
    const bytes = new Uint8Array(binary.length);
    for(let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i) & 255;
    return bytes;
  }

  function dataUrlBytes(dataUrl){
    const text = String(dataUrl || '');
    const comma = text.indexOf(',');
    if(!/^data:/i.test(text) || comma < 0) return new Uint8Array(0);
    const body = text.slice(comma + 1);
    try {
      if(/;base64$/i.test(text.slice(0, comma))) return binaryBytes(atob(body));
      return binaryBytes(body.replace(/%([0-9a-f]{2})/gi, (match, code) => String.fromCharCode(parseInt(code, 16))));
    } catch(e){
      return new Uint8Array(0);
    }
  }

  function cardWidthIn(size, orientation){
    const card = size || ArtCards.sizeFor();
    if(card.isSquare) return card.shortIn;
    return orientation === 'landscape' ? card.longIn : card.shortIn;
  }

  function logoLowDpi({ naturalWidth, isSvg, size, cardWidthIn: widthIn }){
    if(isSvg || !(naturalWidth > 0)) return null;
    const placedIn = widthIn * ((ArtCards.LOGO_WIDTHS[size] || ArtCards.LOGO_WIDTHS[LOGO_DEFAULT.size]) / 100);
    if(!(placedIn > 0)) return null;
    const dpi = Math.round(naturalWidth / placedIn);
    return dpi < ArtCards.LOGO_MIN_DPI ? dpi : null;
  }

  function isSvgLogo(logo){
    return !!logo && SVG_DATA.test(String(logo.dataUrl || ''));
  }

  function titleChanged(design){
    if(design && isObject(design.ownBack)) design.ownBack.line2_text = String(design.title == null ? '' : design.title).trim();
    return design;
  }

  function designLogo(sharedLogo, design){
    if(design && design.ownBack) return copyOf(design.ownLogo);
    return copyOf(sharedLogo);
  }

  function printBack(sharedBack, sharedLogo, design){
    const logo = designLogo(sharedLogo, design);
    return {
      back: ArtCards.designBack(sharedBack, design),
      logo: logo ? Object.assign(logo, { layout: design.logoLayout }) : null
    };
  }

  function startOwn(sharedBack, sharedLogo, design){
    return { ownBack: ArtCards.designBack(sharedBack, Object.assign({}, design, { ownBack: null })), ownLogo: copyOf(sharedLogo) };
  }

  function stopOwn(){
    return { ownBack: null, ownLogo: null };
  }

  function textPct(textHeight, cardHeight){
    return cardHeight > 0 ? (Number(textHeight) || 0) / cardHeight * 100 : null;
  }

  function logoLayout(sharedBack, sharedLogo, design, measured){
    const logo = designLogo(sharedLogo, design);
    if(!logo) return null;
    return ArtCards.logoMeta({
      position: logo.position,
      size: logo.size,
      textPosition: ArtCards.designBack(sharedBack, design).position,
      textPct: Number.isFinite(measured) ? measured : null
    });
  }

  function previewFontSize(size, config){
    const scale = Number(config && config.previewScale) || 1;
    const min = Number(config && config.previewMin) || 0;
    return Math.max(min, Math.round((Number(size) || 0) * scale));
  }

  function browserStorage(){
    try {
      return window.localStorage;
    } catch(e){
      return null;
    }
  }

  function readJson(node){
    if(!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch(e){
      return null;
    }
  }

  function readDataUrl(file){
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  function findRoot(panelEl){
    const inside = panelEl.querySelector('[data-pb-part="back"]');
    if(inside) return inside;
    const host = panelEl.closest('[data-pb-id]');
    const id = host ? host.getAttribute('data-pb-id') : null;
    return Array.from(document.querySelectorAll('[data-pb-part="back"]'))
      .find(node => node.getAttribute('data-pb-id') === id) || null;
  }

  function mount(panelEl, ctx){
    if(typeof document === 'undefined' || !panelEl || !ctx || typeof ctx.update !== 'function') return null;
    const root = findRoot(panelEl);
    if(!root) return null;
    if(root.parentNode !== panelEl) panelEl.appendChild(root);
    root.hidden = false;

    const part = name => root.querySelector('[data-pb-bk="' + name + '"]');
    const parts = (attribute) => Array.from(root.querySelectorAll('[' + attribute + ']'));
    const setText = (node, text) => { if(node) node.textContent = text; };
    const config = readJson(root.querySelector('script[data-pb-bk-config]')) || {};
    const strings = readJson(root.querySelector('script[data-pb-bk-strings]')) || {};
    const t = (key, values) => fill(strings[key], values);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const listen = (target, type, handler) => {
      if(target) target.addEventListener(type, event => { if(alive) handler(event); }, controller ? { signal: controller.signal } : false);
    };
    const el = {
      card: part('card'),
      text: part('text'),
      logo: part('logo'),
      hint: part('hint'),
      collapsed: part('collapsed'),
      expanded: part('expanded'),
      sharedNote: part('shared-note'),
      title: part('title'),
      sameLabel: part('same-label'),
      sameSummary: part('same-summary'),
      edit: part('edit'),
      positionTiles: parts('data-pb-bk-pos'),
      logoAdd: part('logo-add'),
      logoSet: part('logo-set'),
      logoThumb: part('logo-thumb'),
      logoInputs: parts('data-pb-bk-logo-input'),
      logoRemove: part('logo-remove'),
      logoPositions: parts('data-pb-bk-lpos'),
      logoSizes: parts('data-pb-bk-lsize'),
      logoNote: part('logo-note'),
      logoNoteText: part('logo-note-text'),
      remember: part('remember'),
      clear: part('clear'),
      ownWrap: part('own-wrap'),
      own: part('own'),
      toast: part('toast'),
      toastText: part('toast-text'),
      toastClose: part('toast-close'),
      announce: part('announce')
    };
    const lines = LINES.map(n => {
      const box = root.querySelector('[data-pb-bk-line="' + n + '"]');
      const field = name => box ? box.querySelector('[data-pb-bk-field="' + name + '"]') : null;
      return {
        n,
        input: field('text'),
        font: field('font'),
        size: field('size'),
        style: field('style'),
        preview: root.querySelector('[data-pb-bk-preview="' + n + '"]')
      };
    });
    let alive = true;
    let expanded = false;
    let pickToken = 0;
    let logoSrc = '';
    let thumbSrc = '';
    let natural = { url: '', width: 0 };
    let toastTimer = null;
    const keying = new Set();
    const track = busyTracker(on => { if(alive) ctx.update({ busy: on }); });

    function design(){
      return ctx.design || {};
    }

    function isOwn(){
      return !!design().ownBack;
    }

    function activeBack(){
      return isOwn() ? design().ownBack : (ctx.sharedBack || defaults());
    }

    function activeLogo(){
      return designLogo(ctx.sharedLogo, design());
    }

    function backField(){
      return isOwn() ? 'ownBack' : 'sharedBack';
    }

    function logoField(){
      return isOwn() ? 'ownLogo' : 'sharedLogo';
    }

    function setBack(changes){
      ctx.update({ [backField()]: Object.assign({}, activeBack(), changes) });
    }

    function setLogo(logo){
      ctx.update({ [logoField()]: logo });
    }

    function option(value, label){
      const tpl = root.querySelector('template[data-pb-bk-tpl="option"]');
      const node = tpl && tpl.content && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
      if(!node) return null;
      node.value = String(value);
      node.textContent = label;
      return node;
    }

    function buildOptions(){
      lines.forEach(line => {
        if(line.font && !line.font.options.length){
          line.font.append(...ArtCards.BACK_FONTS.map(font => option(font, font)).filter(Boolean));
        }
        if(line.size && !line.size.options.length){
          line.size.append(...ArtCards.BACK_SIZES['line' + line.n].map(size => option(size, t('size_option', { size }))).filter(Boolean));
        }
      });
    }

    function shapeOf(orientation){
      if(ctx.size && ctx.size.isSquare) return 'square';
      return orientation === 'landscape' ? 'landscape' : 'portrait';
    }

    function paintText(back, orientation){
      if(!el.card) return;
      const shape = shapeOf(orientation);
      SHAPES.forEach(name => el.card.classList.toggle('pb-bk__card--' + name, name === shape));
      TEXT_POSITIONS.forEach(pos => el.card.classList.toggle('pb-bk__card--text-' + pos, pos === back.position));
      lines.forEach(line => {
        const node = line.preview;
        if(!node) return;
        const text = String(back['line' + line.n + '_text'] || '');
        const style = back['line' + line.n + '_style'];
        node.textContent = text;
        node.hidden = !text;
        node.style.setProperty('--pb-bk-family', '"' + back['line' + line.n + '_font'] + '"');
        node.style.setProperty('--pb-bk-fs', String(previewFontSize(back['line' + line.n + '_size'], config)));
        node.classList.toggle('pb-bk__line--bold', style === 'bold');
        node.classList.toggle('pb-bk__line--italic', style === 'italic');
      });
    }

    function measureNow(){
      return el.text && el.card ? textPct(el.text.offsetHeight, el.card.clientHeight) : null;
    }

    function paintLogo(logo, layout){
      if(!el.logo) return;
      el.logo.hidden = !logo;
      if(!logo || !layout) return;
      if(logoSrc !== logo.dataUrl){
        logoSrc = logo.dataUrl;
        el.logo.src = logo.dataUrl;
      }
      LOGO_POSITIONS.forEach(pos => el.logo.classList.toggle('pb-bk__logo--' + pos, pos === layout.position));
      el.logo.style.setProperty('--pb-bk-logo-w', layout.width_pct + '%');
      el.logo.style.setProperty('--pb-bk-inset', layout.inset_pct + '%');
      el.logo.style.setProperty('--pb-bk-pad', String(layout.padding_px));
    }

    function paintPreview(){
      const d = design();
      const back = ArtCards.designBack(ctx.sharedBack || defaults(), d);
      const logo = activeLogo();
      paintText(back, ctx.orientation);
      const layout = logo ? logoLayout(ctx.sharedBack || defaults(), ctx.sharedLogo, d, measureNow()) : null;
      paintLogo(logo, layout);
      setText(el.hint, t('hint', { number: d.number || 1 }));
    }

    function measureTextPct(target){
      if(!alive) return null;
      if(!target) return measureNow();
      paintText(ArtCards.designBack(ctx.sharedBack || defaults(), target), target.orientation);
      const measured = measureNow();
      paintPreview();
      return measured;
    }

    function setValue(node, value){
      if(node && document.activeElement !== node && node.value !== value) node.value = value;
    }

    function pressed(nodes, attribute, value){
      nodes.forEach(node => node.setAttribute('aria-pressed', String(node.getAttribute(attribute) === value)));
    }

    function word(prefix, value){
      return strings[prefix + value] != null ? t(prefix + value) : String(value || '');
    }

    function summary(){
      const back = ctx.sharedBack || defaults();
      const logo = ctx.sharedLogo;
      const items = [back.line1_text, back.line3_text].map(text => String(text || '').trim()).filter(Boolean);
      items.push(logo
        ? t('summary_logo', { position: word('pos_', logo.position), size: word('size_', logo.size) })
        : t('summary_nologo'));
      items.push(t('summary_text', { position: word('pos_', back.position) }));
      return items.join(t('summary_join'));
    }

    function paintFields(){
      const d = design();
      const back = activeBack();
      const title = String(d.title == null ? '' : d.title);
      setValue(el.title, title);
      lines.forEach(line => {
        setValue(line.input, line.n === 2 ? title : String(back['line' + line.n + '_text'] || ''));
        if(line.font) line.font.value = String(back['line' + line.n + '_font']);
        if(line.size) line.size.value = String(back['line' + line.n + '_size']);
        if(line.style) line.style.value = String(back['line' + line.n + '_style']);
      });
      pressed(el.positionTiles, 'data-pb-bk-pos', back.position);
      const firstTitle = String(ctx.firstTitle || '').trim();
      setText(el.sameLabel, firstTitle ? t('same_named', { title: firstTitle }) : t('same_first'));
      setText(el.sameSummary, summary());
    }

    function ensureKey(field, logo){
      if(!logo || logo.key || !logo.dataUrl || keying.has(logo.dataUrl)) return;
      keying.add(logo.dataUrl);
      track(logoKey(dataUrlBytes(logo.dataUrl)).then(key => {
        const now = field === 'ownLogo' ? design().ownLogo : ctx.sharedLogo;
        if(alive && now && now.dataUrl === logo.dataUrl && !now.key) ctx.update({ [field]: Object.assign({}, now, { key }) });
      }, () => {}));
    }

    function paintLogoControls(){
      const logo = activeLogo();
      if(el.logoAdd) el.logoAdd.hidden = !!logo;
      if(el.logoSet) el.logoSet.hidden = !logo;
      if(el.logoThumb && logo && thumbSrc !== logo.dataUrl){
        thumbSrc = logo.dataUrl;
        el.logoThumb.src = logo.dataUrl;
      }
      pressed(el.logoPositions, 'data-pb-bk-lpos', logo ? logo.position : '');
      pressed(el.logoSizes, 'data-pb-bk-lsize', logo ? logo.size : '');
      const dpi = logo ? logoLowDpi({
        naturalWidth: natural.url === logo.dataUrl ? natural.width : 0,
        isSvg: isSvgLogo(logo),
        size: logo.size,
        cardWidthIn: cardWidthIn(ctx.size, ctx.orientation)
      }) : null;
      if(el.logoNote) el.logoNote.hidden = dpi == null;
      setText(el.logoNoteText, dpi == null ? '' : t('logo_low', { dpi }));
      ensureKey('sharedLogo', ctx.sharedLogo);
      ensureKey('ownLogo', design().ownLogo);
    }

    function render(){
      if(!alive || !ctx.design) return;
      const own = isOwn();
      const first = !!ctx.isFirst;
      const open = first || own || expanded;
      if(el.collapsed) el.collapsed.hidden = open;
      if(el.expanded) el.expanded.hidden = !open;
      if(el.sharedNote) el.sharedNote.hidden = first || own;
      if(el.remember) el.remember.hidden = !first;
      if(el.ownWrap) el.ownWrap.hidden = first;
      if(el.own) el.own.checked = own;
      paintFields();
      paintLogoControls();
      paintPreview();
    }

    function hideToast(){
      clearTimeout(toastTimer);
      if(el.toast) el.toast.hidden = true;
    }

    function showToast(message){
      setText(el.announce, message);
      if(!el.toast) return;
      setText(el.toastText, message);
      el.toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(hideToast, Number(config.toastMs) || 0);
    }

    function pickLogo(input){
      const file = input.files && input.files[0];
      input.value = '';
      if(!file || !LOGO_TYPES.includes(file.type)) return;
      pickToken += 1;
      const token = pickToken;
      track(readDataUrl(file)
        .then(dataUrl => logoKey(dataUrlBytes(dataUrl)).then(key => ({ dataUrl, key }), () => ({ dataUrl, key: '' })))
        .then(({ dataUrl, key }) => {
          if(!alive || token !== pickToken || !LOGO_DATA.test(dataUrl)) return;
          const current = activeLogo();
          keying.add(dataUrl);
          setLogo({
            key,
            dataUrl,
            position: current ? current.position : LOGO_DEFAULT.position,
            size: current ? current.size : LOGO_DEFAULT.size
          });
        }, () => {}));
    }

    function toggleOwn(on){
      const d = design();
      ctx.update(on ? startOwn(ctx.sharedBack || defaults(), ctx.sharedLogo, d) : stopOwn());
    }

    function clearRemembered(){
      clearSaved(browserStorage());
      ctx.update({ sharedBack: defaults(), sharedLogo: null });
      showToast(t('cleared'));
    }

    function bind(){
      listen(el.title, 'input', () => ctx.update({ title: el.title.value }));
      lines.forEach(line => {
        listen(line.input, 'input', () => {
          if(line.n === 2) ctx.update({ title: line.input.value });
          else setBack({ ['line' + line.n + '_text']: line.input.value.trim() });
        });
        listen(line.input, 'change', render);
        listen(line.font, 'change', () => {
          if(ArtCards.BACK_FONTS.includes(line.font.value)) setBack({ ['line' + line.n + '_font']: line.font.value });
        });
        listen(line.size, 'change', () => {
          const size = validSize(line.n, line.size.value);
          if(size != null) setBack({ ['line' + line.n + '_size']: size });
        });
        listen(line.style, 'change', () => {
          if(STYLES.includes(line.style.value)) setBack({ ['line' + line.n + '_style']: line.style.value });
        });
      });
      el.positionTiles.forEach(tile => listen(tile, 'click', () => {
        const pos = tile.getAttribute('data-pb-bk-pos');
        if(TEXT_POSITIONS.includes(pos)) setBack({ position: pos });
      }));
      el.logoInputs.forEach(input => listen(input, 'change', () => pickLogo(input)));
      listen(el.logoRemove, 'click', () => {
        pickToken += 1;
        setLogo(null);
        if(el.logoInputs[0]) el.logoInputs[0].focus();
      });
      el.logoPositions.forEach(button => listen(button, 'click', () => {
        const logo = activeLogo();
        const pos = button.getAttribute('data-pb-bk-lpos');
        if(logo && LOGO_POSITIONS.includes(pos)) setLogo(Object.assign(logo, { position: pos }));
      }));
      el.logoSizes.forEach(button => listen(button, 'click', () => {
        const logo = activeLogo();
        const size = button.getAttribute('data-pb-bk-lsize');
        if(logo && LOGO_SIZES.includes(size)) setLogo(Object.assign(logo, { size }));
      }));
      listen(el.logo, 'load', () => {
        natural = { url: logoSrc, width: el.logo.naturalWidth || 0 };
        render();
      });
      listen(el.edit, 'click', () => {
        expanded = true;
        render();
        if(lines[0].input) lines[0].input.focus();
      });
      listen(el.own, 'change', () => toggleOwn(el.own.checked));
      listen(el.clear, 'click', clearRemembered);
      listen(el.toastClose, 'click', hideToast);
    }

    function unmount(){
      if(!alive) return;
      alive = false;
      pickToken += 1;
      hideToast();
      if(controller) controller.abort();
    }

    buildOptions();
    bind();
    render();
    return { measureTextPct, render, unmount };
  }

  return {
    BACK_KEY,
    LOGO_KEY,
    LOGO_TYPES,
    LOGO_POSITIONS,
    LOGO_SIZES,
    TEXT_POSITIONS,
    STYLES,
    defaults,
    cleanBack,
    cleanLogo,
    loadSaved,
    save,
    clearSaved,
    logoKey,
    busyTracker,
    dataUrlBytes,
    cardWidthIn,
    logoLowDpi,
    titleChanged,
    designLogo,
    printBack,
    startOwn,
    stopOwn,
    textPct,
    logoLayout,
    previewFontSize,
    mount
  };
});
